// Billing on the API: the balance and this month's use from GET /workspaces/{ws}/credits, the ledger from
// .../credits/entries, the packs from GET /pricing (F7); the plan, its seats, invoices and the ways to pay from
// .../plan, .../members, .../invoices and .../billing/* (F8). The mapping is pure and tested; `createBillingSource`
// only fetches.
import { type Api, ApiError } from "../api.ts";
import { isDepartment } from "../departments.ts";
import { num } from "../lib.ts";
import { getPricing, parsePricing } from "./money.ts";
import type {
  Balance,
  BillingSource,
  Invoice,
  LedgerEntry,
  LedgerPage,
  Pack,
  PlanInfo,
  Seat,
  SpendDept,
} from "./sources.ts";

/** Ledger lines per page ("Show more" asks for the next). */
export const LEDGER_PAGE = 20;

/** The body of GET /workspaces/{ws}/credits as a `Balance`. Departments the app does not know count as "other". */
export function balanceFromApi(raw: unknown): Balance {
  const r = (raw ?? {}) as Record<string, unknown>;
  const used: Partial<Record<SpendDept, number>> = {};
  const byDept = (r.used_by_department ?? {}) as Record<string, unknown>;
  for (const [key, value] of Object.entries(byDept)) {
    const n = num(value);
    if (n == null || n <= 0) continue;
    const dept: SpendDept = isDepartment(key) ? key : "other";
    used[dept] = (used[dept] ?? 0) + n;
  }
  const credits = num(r.balance) ?? 0;
  return {
    billing: r.billing === "unmetered" ? "unmetered" : "credits",
    credits,
    available: num(r.available) ?? credits,
    usedCredits: num(r.used_this_month) ?? Object.values(used).reduce((a, b) => a + b, 0),
    usedByDept: used,
  };
}

const KINDS = new Set<LedgerEntry["kind"]>([
  "grant",
  "spend",
  "purchase",
  "adjustment",
  "plan",
  "expire",
  "storage",
]);

/** The body of GET /workspaces/{ws}/credits/entries as a `LedgerPage`. */
export function ledgerFromApi(raw: unknown): LedgerPage {
  const r = (raw ?? {}) as { items?: unknown; next_cursor?: unknown };
  const items: LedgerEntry[] = [];
  for (const i of Array.isArray(r.items) ? r.items : []) {
    const e = (i ?? {}) as Record<string, unknown>;
    const credits = num(e.credits);
    if (typeof e.id !== "string" || credits == null || !KINDS.has(e.kind as LedgerEntry["kind"])) continue;
    items.push({
      id: e.id,
      kind: e.kind as LedgerEntry["kind"],
      credits,
      note: typeof e.note === "string" && e.note.trim() ? e.note.trim() : null,
      at: typeof e.created_at === "string" ? e.created_at : "",
    });
  }
  return { items, next: typeof r.next_cursor === "string" && r.next_cursor ? r.next_cursor : null };
}

/** The packs of GET /pricing, model cost and our margin apart. */
export function packsFromApi(raw: unknown): Pack[] {
  return (parsePricing(raw)?.packs ?? []).map((p) => ({
    credits: p.credits,
    priceEur: p.priceEur,
    modelEur: p.modelEur,
    marginEur: p.marginEur,
  }));
}

const bool = (v: unknown): boolean => v === true;
const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);

/** The body of GET /workspaces/{ws}/plan as a `PlanInfo`. Missing parts read as open (nothing known, nothing limited). */
export function planFromApi(raw: unknown): PlanInfo {
  const r = (raw ?? {}) as Record<string, unknown>;
  const seats = (r.seats ?? {}) as Record<string, unknown>;
  const storage = (r.storage ?? {}) as Record<string, unknown>;
  const limits = (r.limits ?? {}) as Record<string, unknown>;
  const metered = r.billing !== "unmetered";
  const plan = r.plan === "free" || r.plan === "team" || r.plan === "custom" ? r.plan : null;
  const status =
    r.status === "active" || r.status === "past_due" || r.status === "canceled" ? r.status : null;
  return {
    billing: metered ? "credits" : "unmetered",
    plan: metered ? plan : null,
    status,
    seatsUsed: num(seats.used) ?? 0,
    seatsPaid: num(seats.paid),
    seatEur: num(r.seat_eur) ?? 0,
    seatCredits: num(r.seat_credits) ?? 0,
    renewsAt: str(r.renews_at),
    cancelAtPeriodEnd: bool(r.cancel_at_period_end),
    graceUntil: str(r.grace_until),
    planCreditsPerMonth: num(r.plan_credits_per_month) ?? 0,
    planCreditsLeft: num(r.plan_credits_left) ?? 0,
    rolloverCap: num(r.rollover_cap) ?? 0,
    storageUsedBytes: num(storage.used_bytes) ?? 0,
    storageIncludedBytes: num(storage.included_bytes),
    limits: {
      productions: num(limits.productions),
      productionsUsed: num(limits.productions_used) ?? 0,
      final: metered ? bool(limits.final) : true,
    },
    canBuyPacks: bool(r.can_buy_packs),
    canManage: bool(r.can_manage),
    billingAvailable: bool(r.billing_available),
  };
}

/** The seat holders: members whose role takes a seat. A member without a name shows their address. */
export function seatsFromMembers(raw: unknown, seatRoles: readonly string[]): Seat[] {
  const items = (raw as { items?: unknown } | null)?.items;
  const out: Seat[] = [];
  for (const i of Array.isArray(items) ? items : []) {
    const m = (i ?? {}) as Record<string, unknown>;
    const userId = str(m.user_id);
    const role = str(m.role);
    if (!userId || !role || !seatRoles.includes(role)) continue;
    const name = str(m.display_name)?.trim() || str(m.email) || "Someone";
    out.push({ userId, name, role });
  }
  return out;
}

/** The body of GET /workspaces/{ws}/invoices as `Invoice`s, newest first as the server sent them. */
export function invoicesFromApi(raw: unknown): Invoice[] {
  const items = (raw as { items?: unknown } | null)?.items;
  const out: Invoice[] = [];
  for (const i of Array.isArray(items) ? items : []) {
    const e = (i ?? {}) as Record<string, unknown>;
    const id = str(e.id);
    const issuedAt = str(e.issued_at);
    const amount = num(e.amount_eur_excl);
    if (!id || !issuedAt || amount == null) continue;
    out.push({
      id,
      number: str(e.number),
      kind: e.kind === "pack" ? "pack" : "plan",
      description: str(e.description),
      amountEurExcl: amount,
      vatEur: num(e.vat_eur) ?? 0,
      status: str(e.status) ?? "",
      pdfUrl: str(e.pdf_url),
      hostedUrl: str(e.hosted_url),
      issuedAt,
    });
  }
  return out;
}

/** A server without the F8 routes (404) or without a payment provider (501): the answer is "nothing". */
const isAbsent = (e: unknown): boolean => e instanceof ApiError && (e.status === 404 || e.status === 501);

const SEAT_ROLES = ["owner", "producer", "director", "maker"];

export function createBillingSource(api: Api): BillingSource {
  const w = (ws: string) => `/workspaces/${ws}`;
  const url = async (method: "POST", path: string, body: unknown) => {
    const r = await api.request<{ url?: unknown }>(method, path, { body });
    const u = str(r?.url);
    if (!u) throw new Error("The payment page did not open. Try again in a moment.");
    return u;
  };
  return {
    balance: async (ws) => balanceFromApi(await api.request("GET", `${w(ws)}/credits`)),
    plan: async (ws, o) => {
      let plan: PlanInfo;
      try {
        plan = planFromApi(await api.request("GET", `${w(ws)}/plan`));
      } catch (e) {
        if (isAbsent(e)) return null;
        throw e;
      }
      // who holds the seats: the members with a seat role (viewer-level, like the plan itself); the list is a
      // nicety, so a failure leaves it empty rather than hiding the plan
      const roles = getPricing().hosted?.seatRoles ?? SEAT_ROLES;
      const seats =
        o?.seats !== false && plan.plan && plan.plan !== "free"
          ? await api.request("GET", `${w(ws)}/members`, { query: { limit: 200 } }).then(
              (m) => seatsFromMembers(m, roles),
              () => [],
            )
          : [];
      return { plan, seats };
    },
    packs: async () => packsFromApi(await api.request("GET", "/pricing")),
    invoices: async (ws) => {
      try {
        return invoicesFromApi(await api.request("GET", `${w(ws)}/invoices`));
      } catch (e) {
        if (isAbsent(e)) return [];
        throw e;
      }
    },
    checkout: (ws, seats) => url("POST", `${w(ws)}/billing/checkout`, { seats }),
    buyPack: (ws, credits) => url("POST", `${w(ws)}/billing/packs`, { credits }),
    setSeats: async (ws, seats) =>
      planFromApi(await api.request("PATCH", `${w(ws)}/billing/seats`, { body: { seats } })),
    portal: (ws) => url("POST", `${w(ws)}/billing/portal`, {}),
    entries: async (ws, cursor) =>
      ledgerFromApi(
        await api.request("GET", `${w(ws)}/credits/entries`, {
          query: { limit: LEDGER_PAGE, cursor: cursor ?? undefined },
        }),
      ),
  };
}
